/**
 * The priced Bill of Materials (APP_SPEC box 4), worn in Ant Design:
 * every quantity is measured, every dollar carries provenance, and
 * UNPRICED rows are shown prominently -- the engine never fabricates
 * a price, and the honesty is the feature. The app-wide filter
 * narrows the visible rows on the axes line items carry (category,
 * ifc_class).
 */
import { useMemo } from 'react';
import { useSearch } from '@tanstack/react-router';
import { Alert, Button, Space, Statistic, Table, Typography } from 'antd';
import type { ElementFilter } from '../../../api';
import { filterIsActive } from '../../../api';
import { ABSENCE, AbsenceBadge, ProvenancePill } from '../../../strata';
import { useRefreshTakeoff, useTakeoff, type TakeoffLineItem } from '../queries';

/** UNPRICED is the ABSENCE of any rate (amber, never violation-red);
 * a priced line wears its provenance tier as the provenance pill. */
function provenanceTag(item: TakeoffLineItem) {
  if (item.unit_price === null)
    return (
      <AbsenceBadge
        label="UNPRICED"
        reason={item.rate_provenance ?? 'no verified rate matched this line'}
      />
    );
  return <ProvenancePill provenance={item.rate_provenance ?? ''} />;
}

const money = (v: number) =>
  `$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const columns = [
  {
    title: 'Item',
    key: 'item',
    render: (item: TakeoffLineItem) => (
      <>
        <div>{item.sku}</div>
        <Typography.Text type="secondary" style={{ fontSize: 11 }}>
          {item.ifc_class}
          {item.category ? ` · ${item.category}` : ''}
        </Typography.Text>
      </>
    ),
  },
  {
    title: 'Qty',
    key: 'qty',
    width: 140,
    render: (item: TakeoffLineItem) => (
      <>
        {item.quantity.toLocaleString(undefined, { maximumFractionDigits: 1 })} {item.unit}
        {item.sticks !== null && (
          <Typography.Text type="secondary" style={{ fontSize: 11, display: 'block' }}>
            {item.sticks} sticks
          </Typography.Text>
        )}
      </>
    ),
  },
  {
    title: 'Unit price',
    key: 'unit',
    width: 110,
    render: (item: TakeoffLineItem) =>
      item.unit_price !== null ? money(item.unit_price) : '—',
  },
  {
    title: 'Total',
    key: 'total',
    width: 110,
    render: (item: TakeoffLineItem) =>
      item.total_cost !== null ? money(item.total_cost) : '—',
  },
  {
    title: 'Provenance',
    key: 'provenance',
    width: 150,
    render: provenanceTag,
  },
];

export function TakeoffPanel({ projectId }: { projectId: string }) {
  const { data, isLoading, isError } = useTakeoff(projectId);
  const refresh = useRefreshTakeoff(projectId);
  const filter = useSearch({ strict: false }) as ElementFilter;
  const filtered = filterIsActive(filter);

  const result = data && !data.queued ? data.result : null;
  const rows = useMemo(() => {
    if (!result) return [];
    return result.line_items.filter((item) => {
      if (filter.category?.length && !filter.category.includes(item.category ?? 'OTHER')) {
        return false;
      }
      if (filter.ifc_class && item.ifc_class !== filter.ifc_class) return false;
      return true;
    });
  }, [result, filter]);

  if (isError) {
    return <Alert type="error" showIcon title="Takeoff failed to load." />;
  }
  if (isLoading || !data) {
    return <Typography.Text type="secondary">Loading takeoff…</Typography.Text>;
  }
  if (data.queued) {
    return (
      <Typography.Text type="secondary">
        Takeoff is materializing on the worker — this page updates itself.
      </Typography.Text>
    );
  }

  const shownCost = rows.reduce((sum, r) => sum + (r.total_cost ?? 0), 0);
  const shownUnpriced = rows.filter((r) => r.unit_price === null).length;

  return (
    <Space orientation="vertical" size="middle" style={{ width: '100%' }}>
      <Space align="baseline" size="large" wrap>
        <Statistic value={shownCost} precision={2} prefix="$" />
        <Typography.Text type="secondary">
          {filtered
            ? `${rows.length} filtered lines`
            : `${rows.length} lines · ${result!.element_count} elements`}
          {shownUnpriced > 0 && (
            <Typography.Text style={{ color: ABSENCE }}> · {shownUnpriced} unpriced</Typography.Text>
          )}
          {!filtered &&
            result!.total_sticks > 0 &&
            ` · ${result!.total_sticks} sticks (${result!.total_waste_inches.toFixed(0)}" waste)`}
        </Typography.Text>
        <Button onClick={() => refresh.mutate()} loading={refresh.isPending}>
          Refresh prices
        </Button>
      </Space>
      <Table
        rowKey={(item) => `${item.ifc_class}:${item.sku}`}
        size="small"
        columns={columns}
        dataSource={rows}
        pagination={false}
      />
    </Space>
  );
}
